import React from "react";
import { FolderOpen, Settings2, Bell } from "lucide-react";
import { CARD } from "@/styles/shared";
import { timeAgo } from "@/utils/timeAgo";

const DASH_CARD = {
  ...CARD,
  background: "rgba(255,255,255,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.7)",
};

const HEALTH_COLOR = { green: "#16a34a", yellow: "#d97706", red: "#dc2626" };

export default function ProjectsGrid({ projects, onOpenKanban, onSettings, onCreateProject }) {
  const atLimit = projects.length >= 6;
  return (
    <div style={{ ...DASH_CARD, overflow: "hidden" }}>
      <div style={{
        padding: "0.875rem 1.25rem",
        borderBottom: "1px solid rgba(26,77,46,0.1)",
        display: "flex", alignItems: "center", gap: "0.5rem",
      }}>
        <FolderOpen size={16} color="#3d6b1f" />
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>
          Active Projects
        </span>
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#3d6b1f", fontSize: "0.85rem", fontWeight: 600 }}>
          ({projects.length}/6)
        </span>
      </div>

      <div style={{ padding: "0.875rem 1rem" }}>
        {projects.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
            gap: "0.75rem",
            marginBottom: "0.75rem",
          }}>
            {projects.map((p) => (
              <ProjectHealthCard
                key={p.id}
                project={p}
                onClick={() => onOpenKanban && onOpenKanban(p)}
                onSettings={() => onSettings && onSettings(p)}
              />
            ))}
          </div>
        )}
        <button
          data-testid="add-project-btn-grid"
          onClick={onCreateProject}
          disabled={atLimit}
          style={{
            width: "100%", padding: "0.55rem",
            borderRadius: "0.625rem",
            border: atLimit ? "1px solid rgba(26,77,46,0.1)" : "1.5px dashed rgba(26,77,46,0.3)",
            background: "transparent",
            color: atLimit ? "#9ca3af" : "#3d6b1f",
            fontFamily: "'Manrope', sans-serif", fontSize: "0.85rem", fontWeight: 600,
            cursor: atLimit ? "not-allowed" : "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { if (!atLimit) e.currentTarget.style.background = "rgba(61,107,31,0.06)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          {atLimit ? "6/6 limit — archive a project to add more" : "+ Add New Project"}
        </button>
      </div>
    </div>
  );
}

function ProjectHealthCard({ project, onClick, onSettings }) {
  const healthColor = HEALTH_COLOR[project.health] || "#6b7280";
  const isWork = project.category === "work";

  return (
    <div
      data-testid={`project-health-card-${project.id}`}
      onClick={onClick}
      style={{
        background: "rgba(255,255,255,0.65)",
        border: "1px solid rgba(255,255,255,0.6)",
        borderRadius: "0.75rem",
        padding: "0.875rem",
        cursor: "pointer",
        transition: "background 0.18s, transform 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.92)";
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(26,77,46,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.65)";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Top row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.55rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem" }}>
          <div style={{
            width: "8px", height: "8px", borderRadius: "50%",
            background: healthColor,
            boxShadow: `0 0 6px ${healthColor}88`,
            flexShrink: 0,
          }} />
          <span style={{ fontSize: "1.25rem", lineHeight: 1 }}>{project.emoji}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <span style={{
            padding: "0.1rem 0.4rem", borderRadius: "2rem", fontSize: "0.6rem", fontWeight: 700,
            fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.04em",
            background: isWork ? "rgba(61,107,31,0.1)" : "rgba(212,165,116,0.15)",
            color: isWork ? "#3d6b1f" : "#92400e",
            border: isWork ? "1px solid rgba(61,107,31,0.2)" : "1px solid rgba(212,165,116,0.35)",
          }}>
            {project.category}
          </span>
          <button
            data-testid={`project-settings-grid-${project.id}`}
            onClick={(e) => { e.stopPropagation(); onSettings(); }}
            style={{
              background: "rgba(26,77,46,0.06)", border: "1px solid rgba(26,77,46,0.12)",
              borderRadius: "0.35rem", width: "22px", height: "22px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#6b7280", transition: "background 0.15s, color 0.15s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(26,77,46,0.14)"; e.currentTarget.style.color = "#1A4D2E"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(26,77,46,0.06)"; e.currentTarget.style.color = "#6b7280"; }}
          >
            <Settings2 size={11} />
          </button>
        </div>
      </div>

      {/* Title */}
      <p style={{
        fontFamily: "'Manrope', sans-serif", fontWeight: 700, color: "#1A4D2E",
        fontSize: "0.875rem", margin: "0 0 0.2rem",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {project.title}
      </p>

      {/* Last activity */}
      <p style={{
        fontFamily: "'Manrope', sans-serif",
        color: project.health === "red" ? "#d97706" : "#6b7280",
        fontSize: "0.7rem", margin: "0 0 0.3rem",
      }}>
        {project.health === "red" ? "⚠ " : ""}Last: {timeAgo(project.last_touched)}
      </p>

      {/* Due date */}
      {project.target_completion && (
        <p style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.68rem", margin: "0 0 0.3rem" }}>
          Due {new Date(project.target_completion).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </p>
      )}

      {/* Task count */}
      <p style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.7rem", margin: "0 0 0.25rem" }}>
        {project.active_task_count} active · {project.total_task_count} total
      </p>
      {project.reminder_count > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", marginBottom: "0.4rem" }}>
          <Bell size={11} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope',sans-serif", color: "#3d6b1f", fontSize: "0.7rem", fontWeight: 600 }}>
            {project.reminder_count} reminder{project.reminder_count !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div style={{ height: "4px", background: "rgba(26,77,46,0.1)", borderRadius: "2px", overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${project.progress}%`,
          background: `linear-gradient(90deg, ${healthColor}, #86c559)`,
          borderRadius: "2px", transition: "width 0.6s ease",
        }} />
      </div>
      {project.total_task_count > 0 && (
        <p style={{
          fontFamily: "'Manrope', sans-serif", color: "#9ca3af",
          fontSize: "0.65rem", margin: "0.25rem 0 0", textAlign: "right",
        }}>
          {project.progress}%
        </p>
      )}
    </div>
  );
}
